/**
 * The body of a failed API call (axios error shape: `error.response.data`), or
 * null when the rejection carries none — a network failure, an abort, or
 * anything that is not an axios error at all.
 */
const getApiErrorData = (error: unknown): Record<string, unknown> | null => {
  if (typeof error !== "object" || error === null) return null;
  const response = (error as { response?: unknown }).response;
  if (typeof response !== "object" || response === null) return null;
  const data = (response as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return null;
  return data as Record<string, unknown>;
};

/**
 * Extracts the machine-readable error code from an API error
 * (axios error shape: `error.response.data.code`).
 * Returns null when the error does not carry a code.
 */
export const getApiErrorCode = (error: unknown): string | null => {
  const code = getApiErrorData(error)?.code;
  return typeof code === "string" ? code : null;
};

/**
 * Extracts the field names an API error blamed
 * (axios error shape: `error.response.data.fields`).
 * Returns null when the rejection names none.
 */
export const getApiErrorFields = (error: unknown): string[] | null => {
  const fields = getApiErrorData(error)?.fields;
  if (!Array.isArray(fields)) return null;

  const named = fields.filter(
    (field): field is string => typeof field === "string",
  );
  return named.length > 0 ? named : null;
};

/**
 * Extracts the HTTP status from an API error (axios error shape:
 * `error.response.status`).
 * Returns null when the rejection carries none (network failure, abort).
 */
export const getApiErrorStatus = (error: unknown): number | null => {
  if (typeof error !== "object" || error === null) return null;
  const response = (error as { response?: unknown }).response;
  if (typeof response !== "object" || response === null) return null;
  const status = (response as { status?: unknown }).status;
  return typeof status === "number" ? status : null;
};

/** Statuses the API answers when a service behind it could not be reached. */
const UPSTREAM_UNAVAILABLE_STATUSES = new Set([502, 503, 504]);

/**
 * Whether a failed API call means "something behind the API is down right now"
 * (a third-party registry, a provider) rather than "what you asked for does
 * not exist". The two must never read the same to a user: one is worth a retry
 * in a minute, the other is not.
 */
export const isUpstreamUnavailableError = (error: unknown): boolean => {
  const status = getApiErrorStatus(error);
  return status !== null && UPSTREAM_UNAVAILABLE_STATUSES.has(status);
};
